/**
 * MasterClassResultModal - Модальное окно для добавления результата мастер-класса
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Textarea,
  Button,
  Group,
  Stack,
  Text,
  Alert,
} from '@mantine/core';
import { IconAlertCircle, IconCalendarEvent } from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';

/**
 * Форматирование даты
 */
const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

/**
 * Получить первый предмет из групп
 */
const getFirstSubjectName = (groups) => {
  if (!groups || groups.length === 0) return 'Без предмета';
  const firstGroup = groups[0];
  return firstGroup.subject_name || 'Без предмета';
};

const MasterClassResultModal = ({
  opened,
  onClose,
  onConfirm,
  masterClass,
  loading = false,
}) => {
  const [resultComment, setResultComment] = useState('');
  const [error, setError] = useState(null);

  // Сбрасываем форму при открытии
  useEffect(() => {
    if (opened) {
      setResultComment('');
      setError(null);
    }
  }, [opened]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!resultComment.trim()) {
      setError('Введите результат мастер-класса');
      return;
    }

    try {
      await onConfirm(resultComment.trim());
    } catch (err) {
      setError(err.message || 'Ошибка при сохранении');
    }
  };

  if (!masterClass) return null;

  const subjectName = getFirstSubjectName(masterClass.groups);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Результат мастер-класса"
      size="md"
      radius="xl"
      styles={{
        content: {
          borderRadius: '16px',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {/* Информация о МК */}
          <Group gap="xs" p="sm" style={{
            backgroundColor: tokens.colors.neutral[1],
            borderRadius: '8px',
          }}>
            <IconCalendarEvent size={18} color={tokens.colors.brand.primary} />
            <Text size="sm">
              МК: {formatDate(masterClass.scheduled_at)} • {subjectName}
            </Text>
          </Group>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          {/* Текст результата */}
          <Textarea
            label="Результат"
            placeholder="Опишите результаты проведённого мастер-класса..."
            required
            autosize
            minRows={4}
            maxRows={8}
            value={resultComment}
            onChange={(e) => setResultComment(e.target.value)}
          />

          {/* Предупреждение */}
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="yellow"
            variant="light"
          >
            После сохранения мастер-класс перейдёт в статус "Проведено"
          </Alert>

          {/* Кнопки */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" loading={loading} color="green">
              Сохранить
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default MasterClassResultModal;
